import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

// Styled Components
const PageContainer = styled.div`
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
  overflow-x: hidden;
`;

// Hero Section
const HeroSection = styled.div`
  position: relative;
  height: 70vh;
  min-height: 500px;
  background-image: url('https://images.unsplash.com/photo-1576678927484-cc9076e4d66e?w=1600&h=800&fit=crop');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(217, 119, 6, 0.85) 0%, rgba(180, 83, 9, 0.75) 100%);
  }
  
  @media (max-width: 768px) {
    height: 50vh;
    min-height: 400px;
    background-attachment: scroll;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  color: white;
  max-width: 800px;
  padding: 0 20px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  @media (min-width: 768px) {
    font-size: 4rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 4.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.95;
  
  @media (min-width: 768px) {
    font-size: 1.4rem;
  }
`;

const HeroButton = styled.button`
  background: white;
  color: #d97706;
  border: none;
  padding: 12px 32px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
    animation: ${pulse} 0.5s ease;
  }
`;

// Content Container
const ContentContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

// Section Styles
const Section = styled.section`
  margin-bottom: 4rem;
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: backwards;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #d97706, #92400e);
    border-radius: 2px;
  }
  
  @media (min-width: 768px) {
    font-size: 2.2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #64748b;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

// Grid Layouts
const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;
  }
`;

const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FourColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  }
`;

// Card Components
const InfoCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: ${slideInLeft} 0.6s ease-out;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 35px rgba(217, 119, 6, 0.15);
  }
`;

const InfoCardTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InfoCardText = styled.p`
  color: #475569;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const ListItem = styled.li`
  color: #475569;
  margin-bottom: 0.75rem;
  line-height: 1.5;
  padding-left: 1.5rem;
  position: relative;
  
  &::before {
    content: '⚖️';
    position: absolute;
    left: 0;
    color: #d97706;
    font-size: 0.9rem;
  }
`;

// BMI Calculator Display
const BMICard = styled.div`
  background: linear-gradient(135deg, #d97706, #b45309);
  border-radius: 20px;
  padding: 1.5rem;
  text-align: center;
  color: white;
  animation: ${bounce} 3s ease-in-out infinite;
`;

const BMIValue = styled.div`
  font-size: 3.5rem;
  font-weight: 800;
  margin: 0.5rem 0;
  
  @media (min-width: 768px) {
    font-size: 4.5rem;
  }
`;

const BMICategory = styled.p`
  font-size: 1.2rem;
  font-weight: 600;
  margin-top: 0.5rem;
`;

const BMIRange = styled.div`
  margin-top: 1rem;
  font-size: 0.9rem;
  opacity: 0.9;
`;

// Warning Boxes
const WarningBox = styled.div`
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border-left: 4px solid #f59e0b;
  padding: 1.5rem;
  border-radius: 15px;
  margin: 1.5rem 0;
`;

const DangerBox = styled.div`
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  border-left: 4px solid #dc2626;
  padding: 1.5rem;
  border-radius: 15px;
  margin: 1.5rem 0;
`;

const SuccessBox = styled.div`
  background: linear-gradient(135deg, #dcfce7, #bbf7d0);
  border-left: 4px solid #22c55e;
  padding: 1.5rem;
  border-radius: 15px;
  margin: 1.5rem 0;
`;

const WarningTitle = styled.h4`
  color: ${props => props.color || '#92400e'};
  font-weight: 700;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`;

const RiskBadge = styled.span`
  background: ${props => props.color || '#d97706'};
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-block;
  margin-right: 0.5rem;
`;

// Doctor Section
const DoctorSection = styled.div`
  background: linear-gradient(135deg, #92400e 0%, #d97706 100%);
  border-radius: 30px;
  padding: 2rem;
  color: white;
  margin-top: 3rem;
  
  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const DoctorGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;
  
  @media (min-width: 768px) {
    grid-template-columns: 280px 1fr;
  }
`;

const DoctorImage = styled.img`
  width: 100%;
  max-width: 250px;
  border-radius: 20px;
  box-shadow: 0 20px 35px rgba(0,0,0,0.3);
  margin: 0 auto;
  display: block;
  
  @media (min-width: 768px) {
    max-width: 100%;
  }
`;

const DoctorContent = styled.div``;

const DoctorName = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #fde68a;
`;

const DoctorTitle = styled.p`
  font-size: 1rem;
  color: #fef3c7;
  margin-bottom: 1rem;
`;

const DoctorDescription = styled.p`
  line-height: 1.7;
  margin-bottom: 1rem;
  color: #fffbeb;
`;

const CTAButton = styled.button`
  background: white;
  color: #d97706;
  border: none;
  padding: 12px 28px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  }
`;

// Image Gallery
const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 2rem 0;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: 15px;
  transition: transform 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (min-width: 768px) {
    height: 200px;
  }
`;

// Stats Component
const StatCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: #d97706;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const StatLabel = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

// Progress Bar
const ProgressContainer = styled.div`
  background: #e2e8f0;
  border-radius: 10px;
  height: 8px;
  margin: 1rem 0;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  background: linear-gradient(135deg, #d97706, #b45309);
  border-radius: 10px;
  height: 100%;
  width: ${props => props.width || '0%'};
  transition: width 1s ease;
`;

// Main Component
const ObesityDiabetesPage = () => {
  const handleAppointment = () => {
    alert('Book an appointment with Dr. Raj Kamal for Weight & Diabetes Management');
  };

  const galleryImages = [
    'https://images.unsplash.com/photo-1576678927484-cc9076e4d66e?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=400&h=300&fit=crop',
  ];

  return (
    <PageContainer>
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>Obesity & Diabetes</HeroTitle>
          <HeroSubtitle>
            Breaking the dangerous cycle | Comprehensive weight and diabetes management by Dr. Raj Kamal
          </HeroSubtitle>
          <HeroButton as={Link} to="/book-appointment">Start Your Journey</HeroButton>
        </HeroContent>
      </HeroSection>

      <ContentContainer>
        {/* Statistics Section */}
        <Section delay="0.05s">
          <FourColumnGrid>
            <StatCard>
              <StatNumber>650M+</StatNumber>
              <StatLabel>Adults worldwide with obesity</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>90%</StatNumber>
              <StatLabel>Type 2 diabetics are overweight or obese</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>5-10%</StatNumber>
              <StatLabel>Weight loss can prevent or reverse diabetes</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>2-4x</StatNumber>
              <StatLabel>Higher risk of diabetes with obesity</StatLabel>
            </StatCard>
          </FourColumnGrid>
        </Section>

        {/* Understanding Obesity Section */}
        <Section delay="0.1s">
          <SectionTitle>Understanding Obesity</SectionTitle>
          <TwoColumnGrid>
            <InfoCard>
              <InfoCardTitle>
                <span>⚖️</span> What is Obesity?
              </InfoCardTitle>
              <InfoCardText>
                Obesity is a complex medical condition characterized by abnormal or excessive fat accumulation that presents health risks. It's not just a cosmetic concern - it's a disease that affects nearly every system in your body.
              </InfoCardText>
              <BMICard>
                <BMIValue>30+</BMIValue>
                <BMICategory>Obese (BMI ≥ 30)</BMICategory>
                <BMIRange>
                  Normal: 18.5-24.9 | Overweight: 25-29.9 | Obese: 30+
                </BMIRange>
              </BMICard>
            </InfoCard>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1576678927484-cc9076e4d66e?w=600&h=400&fit=crop"
                alt="BMI scale"
                style={{ width: '100%', borderRadius: '20px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
              />
              <WarningBox>
                <WarningTitle>⚠️ Obesity is a Disease</WarningTitle>
                <p style={{ color: '#92400e', margin: 0 }}>
                  The American Medical Association recognizes obesity as a chronic disease that requires medical treatment, not just willpower.
                </p>
              </WarningBox>
            </div>
          </TwoColumnGrid>
        </Section>

        {/* The Obesity-Diabetes Connection */}
        <Section delay="0.15s">
          <SectionTitle>The Deadly Link: Obesity & Diabetes</SectionTitle>
          <SectionSubtitle>
            How excess weight drives insulin resistance and type 2 diabetes
          </SectionSubtitle>
          
          <InfoCard>
            <InfoCardTitle>🔄 The Vicious Cycle</InfoCardTitle>
            <InfoCardText>
              Obesity is the single biggest risk factor for type 2 diabetes. About 90% of people with type 2 diabetes are overweight or obese. The relationship works both ways:
            </InfoCardText>
            <div style={{ 
              background: '#fef3c7', 
              padding: '1rem', 
              borderRadius: '15px', 
              textAlign: 'center',
              marginTop: '1rem',
              marginBottom: '1rem'
            }}>
              <p style={{ margin: 0, fontWeight: 'bold', color: '#92400e' }}>
                Excess Fat → Inflammation → Insulin Resistance → High Blood Sugar → Type 2 Diabetes → Weight Gain → More Fat
              </p>
            </div>
            <InfoCardText>
              <strong>Why does this happen?</strong> Excess fat, especially around the abdomen (visceral fat), releases inflammatory chemicals that make your body's cells less responsive to insulin. This forces your pancreas to work harder, eventually leading to burnout and diabetes.
            </InfoCardText>
          </InfoCard>

          <TwoColumnGrid style={{ marginTop: '1.5rem' }}>
            <DangerBox>
              <WarningTitle color="#7f1d1d">🚨 Health Risks of Obesity + Diabetes</WarningTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
                <ListItem style={{ color: '#7f1d1d' }}>Heart disease and stroke (3x higher risk)</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Kidney disease and kidney failure</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Nerve damage (neuropathy)</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Eye damage and blindness</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Fatty liver disease</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Sleep apnea and breathing problems</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Joint problems and osteoarthritis</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Certain cancers (breast, colon, endometrial)</ListItem>
              </ul>
            </DangerBox>
            <SuccessBox>
              <WarningTitle color="#166534">✅ The Good News</WarningTitle>
              <InfoCardText style={{ color: '#166534' }}>
                <strong>Weight loss of just 5-10% of body weight can:</strong>
              </InfoCardText>
              <ul style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
                <ListItem style={{ color: '#166534' }}>Reduce blood sugar and A1C significantly</ListItem>
                <ListItem style={{ color: '#166534' }}>Improve insulin sensitivity</ListItem>
                <ListItem style={{ color: '#166534' }}>Lower blood pressure and cholesterol</ListItem>
                <ListItem style={{ color: '#166534' }}>Reduce or eliminate diabetes medications</ListItem>
                <ListItem style={{ color: '#166534' }}>Put type 2 diabetes into remission</ListItem>
              </ul>
            </SuccessBox>
          </TwoColumnGrid>
        </Section>

        {/* How Obesity Causes Diabetes - Detailed */}
        <Section delay="0.2s">
          <SectionTitle>How Obesity Causes Diabetes</SectionTitle>
          <ThreeColumnGrid>
            <InfoCard>
              <InfoCardTitle>🦠 Chronic Inflammation</InfoCardTitle>
              <InfoCardText>
                Excess fat cells, especially visceral fat around organs, release inflammatory chemicals called cytokines. These interfere with insulin signaling, causing cells to become resistant to insulin.
              </InfoCardText>
              <ProgressContainer>
                <ProgressFill width="75%" />
              </ProgressContainer>
              <InfoCardText style={{ fontSize: '0.85rem' }}>75% increased inflammation in obese individuals</InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🩸 Free Fatty Acids</InfoCardTitle>
              <InfoCardText>
                Excess fat releases free fatty acids into the bloodstream, which accumulate in muscle and liver cells, directly interfering with insulin's ability to move glucose into cells.
              </InfoCardText>
              <ProgressContainer>
                <ProgressFill width="60%" />
              </ProgressContainer>
              <InfoCardText style={{ fontSize: '0.85rem' }}>60% higher free fatty acids in obesity</InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🧬 Hormonal Changes</InfoCardTitle>
              <InfoCardText>
                Fat tissue produces hormones like resistin and leptin that promote insulin resistance. Reduced adiponectin (a protective hormone) further worsens glucose control.
              </InfoCardText>
              <ProgressContainer>
                <ProgressFill width="50%" />
              </ProgressContainer>
              <InfoCardText style={{ fontSize: '0.85rem' }}>50% lower protective hormones</InfoCardText>
            </InfoCard>
          </ThreeColumnGrid>
        </Section>

        {/* Understanding Insulin Resistance */}
        <Section delay="0.25s">
          <SectionTitle>What is Insulin Resistance?</SectionTitle>
          <TwoColumnGrid>
            <InfoCard>
              <InfoCardTitle>💉 The Insulin Problem</InfoCardTitle>
              <InfoCardText>
                Insulin is a hormone that acts like a key, unlocking your cells to allow glucose (sugar) to enter for energy. When you're overweight, your cells become "stiff" and resist insulin's signal - this is insulin resistance.
              </InfoCardText>
              <InfoCardText>
                <strong>What happens:</strong>
              </InfoCardText>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Pancreas works overtime producing more insulin</ListItem>
                <ListItem>Blood sugar remains high despite extra insulin</ListItem>
                <ListItem>Pancreas cells eventually burn out and fail</ListItem>
                <ListItem>Type 2 diabetes develops</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>📊 Signs of Insulin Resistance</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Belly fat (waist > 40" men, > 35" women)</ListItem>
                <ListItem>High blood pressure (≥ 130/85)</ListItem>
                <ListItem>High triglycerides (≥ 150 mg/dL)</ListItem>
                <ListItem>Low HDL cholesterol (&lt; 40 men, &lt; 50 women)</ListItem>
                <ListItem>High fasting blood sugar (≥ 100 mg/dL)</ListItem>
                <ListItem>Skin tags or dark patches (acanthosis nigricans)</ListItem>
                <ListItem>Fatigue after meals</ListItem>
              </ul>
              <WarningBox>
                <WarningTitle>💡 Did You Know?</WarningTitle>
                <p style={{ color: '#92400e', margin: 0 }}>
                  Insulin resistance can exist for 10-15 years before diabetes is diagnosed. This is your window of opportunity for prevention!
                </p>
              </WarningBox>
            </InfoCard>
          </TwoColumnGrid>
        </Section>

        {/* Weight Management Strategies */}
        <Section delay="0.3s">
          <SectionTitle>Effective Weight Management Strategies</SectionTitle>
          <SectionSubtitle>
            Evidence-based approaches to lose weight and improve diabetes
          </SectionSubtitle>
          
          <InfoCard>
            <InfoCardTitle>🥗 Nutrition Strategies</InfoCardTitle>
            <ThreeColumnGrid>
              <div>
                <RiskBadge color="#d97706">1</RiskBadge>
                <strong style={{ color: '#1e293b' }}>Calorie Deficit</strong>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>Reduce 500-750 calories daily</ListItem>
                  <ListItem>Aim for 1-2 lbs weight loss per week</ListItem>
                  <ListItem>Track food intake with apps</ListItem>
                </ul>
              </div>
              <div>
                <RiskBadge color="#d97706">2</RiskBadge>
                <strong style={{ color: '#1e293b' }}>Low-Carb or Mediterranean Diet</strong>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>Limit refined carbs and sugars</ListItem>
                  <ListItem>Increase vegetables and fiber</ListItem>
                  <ListItem>Choose healthy fats (olive oil, nuts, fish)</ListItem>
                </ul>
              </div>
              <div>
                <RiskBadge color="#d97706">3</RiskBadge>
                <strong style={{ color: '#1e293b' }}>Meal Timing</strong>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>Consider intermittent fasting (16:8)</ListItem>
                  <ListItem>Avoid late-night eating</ListItem>
                  <ListItem>Eat protein at every meal</ListItem>
                </ul>
              </div>
            </ThreeColumnGrid>
          </InfoCard>

          <InfoCard style={{ marginTop: '1.5rem' }}>
            <InfoCardTitle>🏃 Physical Activity</InfoCardTitle>
            <ThreeColumnGrid>
              <div>
                <RiskBadge color="#d97706">Aerobic</RiskBadge>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>150-300 minutes/week moderate intensity</ListItem>
                  <ListItem>Walking, swimming, cycling, dancing</ListItem>
                  <ListItem>Burns calories and improves insulin sensitivity</ListItem>
                </ul>
              </div>
              <div>
                <RiskBadge color="#d97706">Strength Training</RiskBadge>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>2-3 sessions per week</ListItem>
                  <ListItem>Builds muscle mass (increases metabolism)</ListItem>
                  <ListItem>Improves glucose uptake by muscles</ListItem>
                </ul>
              </div>
              <div>
                <RiskBadge color="#d97706">NEAT</RiskBadge>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>Non-Exercise Activity Thermogenesis</ListItem>
                  <ListItem>Take stairs, park farther, stand more</ListItem>
                  <ListItem>10,000 steps daily goal</ListItem>
                </ul>
              </div>
            </ThreeColumnGrid>
          </InfoCard>

          <SuccessBox style={{ marginTop: '1.5rem' }}>
            <WarningTitle color="#166534">📈 Success Story</WarningTitle>
            <p style={{ color: '#166534', margin: 0 }}>
              Studies show that people who lose 10-15% of their body weight can put type 2 diabetes into remission without medication!
            </p>
          </SuccessBox>
        </Section>

        {/* Medical Treatments for Obesity */}
        <Section delay="0.35s">
          <SectionTitle>Medical Treatments for Obesity</SectionTitle>
          <ThreeColumnGrid>
            <InfoCard>
              <InfoCardTitle>💊 Anti-Obesity Medications</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem><strong>GLP-1 Agonists:</strong> Semaglutide (Wegovy, Ozempic), Liraglutide (Saxenda)</ListItem>
                <ListItem><strong>Dual GIP/GLP-1:</strong> Tirzepatide (Mounjaro, Zepbound)</ListItem>
                <ListItem><strong>Oral Options:</strong> Metformin, Orlistat, Naltrexone/bupropion</ListItem>
              </ul>
              <InfoCardText style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                These medications can produce 15-20% weight loss and dramatically improve blood sugar control.
              </InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🔬 Bariatric Surgery</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem><strong>Gastric Bypass:</strong> 60-80% excess weight loss</ListItem>
                <ListItem><strong>Sleeve Gastrectomy:</strong> 50-70% excess weight loss</ListItem>
                <ListItem><strong>Gastric Band:</strong> 40-50% excess weight loss</ListItem>
              </ul>
              <InfoCardText style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                Bariatric surgery can put type 2 diabetes into remission in 60-80% of patients.
              </InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🧠 Behavioral Therapy</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Cognitive Behavioral Therapy (CBT)</ListItem>
                <ListItem>Mindful eating training</ListItem>
                <ListItem>Stress management techniques</ListItem>
                <ListItem>Sleep hygiene improvement</ListItem>
              </ul>
              <InfoCardText style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                Addresses emotional eating, food addiction, and lifestyle habits.
              </InfoCardText>
            </InfoCard>
          </ThreeColumnGrid>
        </Section>

        {/* Diabetes Remission */}
        <Section delay="0.4s">
          <SectionTitle>Can Diabetes Be Reversed?</SectionTitle>
          <TwoColumnGrid>
            <InfoCard>
              <InfoCardTitle>🔄 Yes! Diabetes Remission is Possible</InfoCardTitle>
              <InfoCardText>
                <strong>Diabetes remission</strong> means your blood sugar returns to normal levels (A1C &lt; 6.5%) without needing diabetes medication for at least 3 months.
              </InfoCardText>
              <InfoCardText>
                <strong>Key requirements for remission:</strong>
              </InfoCardText>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Significant weight loss (10-15% of body weight)</ListItem>
                <ListItem>Duration of diabetes &lt; 6 years</ListItem>
                <ListItem>Not requiring insulin at diagnosis</ListItem>
                <ListItem>Commitment to lifestyle changes</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>📊 How Weight Loss Achieves Remission</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Reduces fat in the pancreas and liver</ListItem>
                <ListItem>Restores normal insulin production</ListItem>
                <ListItem>Improves insulin sensitivity</ListItem>
                <ListItem>Reduces inflammation</ListItem>
                <ListItem>Normalizes blood sugar regulation</ListItem>
              </ul>
              <ProgressContainer>
                <ProgressFill width="46" />
              </ProgressContainer>
              <InfoCardText style={{ fontSize: '0.85rem' }}>
                The landmark DiRECT study showed 46% remission at 12 months with weight loss program
              </InfoCardText>
            </InfoCard>
          </TwoColumnGrid>
        </Section>

        {/* Meal Planning Guide */}
        <Section delay="0.45s">
          <SectionTitle>Diabetes-Friendly Meal Planning</SectionTitle>
          <FourColumnGrid>
            <InfoCard>
              <InfoCardTitle>🍽️ What to Eat</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Non-starchy vegetables (broccoli, spinach)</ListItem>
                <ListItem>Lean proteins (chicken, fish, tofu)</ListItem>
                <ListItem>Whole grains (quinoa, oats, brown rice)</ListItem>
                <ListItem>Healthy fats (avocado, nuts, olive oil)</ListItem>
                <ListItem>Berries and low-sugar fruits</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🚫 What to Limit</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Sugary drinks (soda, juice, sweet tea)</ListItem>
                <ListItem>White bread, pasta, rice</ListItem>
                <ListItem>Processed snacks and desserts</ListItem>
                <ListItem>Fried foods</ListItem>
                <ListItem>High-sodium processed meats</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🥤 Sample Day</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem><strong>Breakfast:</strong> Greek yogurt + berries + nuts</ListItem>
                <ListItem><strong>Lunch:</strong> Grilled chicken salad + olive oil</ListItem>
                <ListItem><strong>Snack:</strong> Apple + peanut butter</ListItem>
                <ListItem><strong>Dinner:</strong> Salmon + quinoa + roasted vegetables</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>💧 Hydration Tips</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Drink water throughout the day</ListItem>
                <ListItem>Herbal tea (unsweetened)</ListItem>
                <ListItem>Avoid sugary beverages</ListItem>
                <ListItem>Limit alcohol (1 drink/day max)</ListItem>
              </ul>
            </InfoCard>
          </FourColumnGrid>
        </Section>

        {/* Dr. Raj Kamal Section */}
      {/*   <Section delay="0.5s">
          <DoctorSection>
            <DoctorGrid>
              <DoctorImage 
                src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=300&h=300&fit=crop"
                alt="Dr. Raj Kamal"
              />
              <DoctorContent>
                <DoctorName>Dr. Raj Kamal, MD</DoctorName>
                <DoctorTitle>Obesity Medicine & Diabetes Specialist</DoctorTitle>
                <DoctorDescription>
                  With over 15 years of specialized experience in obesity medicine and diabetes management, Dr. Raj Kamal has helped thousands of patients achieve sustainable weight loss and reverse their diabetes. His comprehensive, compassionate approach addresses the root causes of obesity, not just the symptoms.
                </DoctorDescription>
                <DoctorDescription>
                  <strong>How Dr. Raj Kamal can help you:</strong>
                </DoctorDescription>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginBottom: '1rem' }}>
                  <ListItem style={{ color: '#fffbeb' }}>Comprehensive metabolic assessment and body composition analysis</ListItem>
                  <ListItem style={{ color: '#fffbeb' }}>Personalized weight loss plans (diet, exercise, behavior modification)</ListItem>
                  <ListItem style={{ color: '#fffbeb' }}>Medical management with FDA-approved anti-obesity medications</ListItem>
                  <ListItem style={{ color: '#fffbeb' }}>Diabetes medication adjustment and insulin management</ListItem>
                  <ListItem style={{ color: '#fffbeb' }}>Bariatric surgery evaluation and post-surgical support</ListItem>
                  <ListItem style={{ color: '#fffbeb' }}>Continuous glucose monitoring and telehealth follow-ups</ListItem>
                  <ListItem style={{ color: '#fffbeb' }}>Nutritional counseling with registered dietitians</ListItem>
                  <ListItem style={{ color: '#fffbeb' }}>Support groups and ongoing accountability programs</ListItem>
                </ul>
                <CTAButton onClick={handleAppointment}>Start Your Weight Loss Journey →</CTAButton>
              </DoctorContent>
            </DoctorGrid>
          </DoctorSection>
        </Section> */}

        {/* 30-Day Challenge */}
        <Section delay="0.55s">
          <SectionTitle>30-Day Healthy Start Challenge</SectionTitle>
          <ThreeColumnGrid>
            <InfoCard>
              <InfoCardTitle>📅 Week 1: Foundation</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Track everything you eat and drink</ListItem>
                <ListItem>Walk 10 minutes daily</ListItem>
                <ListItem>Drink 8 glasses of water</ListItem>
                <ListItem>Remove sugary drinks</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>📅 Week 2: Build Habits</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Increase walk to 20 minutes</ListItem>
                <ListItem>Add vegetables to every meal</ListItem>
                <ListItem>Eat protein at breakfast</ListItem>
                <ListItem>Sleep 7-8 hours nightly</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>📅 Week 3-4: Accelerate</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Walk 30 minutes or strength train 2x/week</ListItem>
                <ListItem>Try 2 new healthy recipes</ListItem>
                <ListItem>Practice mindful eating</ListItem>
                <ListItem>Join support group</ListItem>
              </ul>
            </InfoCard>
          </ThreeColumnGrid>
        </Section>

        {/* Image Gallery */}
     {/*    <Section delay="0.6s">
          <SectionTitle>Wellness & Care Facilities</SectionTitle>
          <ImageGallery>
            {galleryImages.map((img, index) => (
              <GalleryImage key={index} src={img} alt={`Wellness facility ${index + 1}`} />
            ))}
          </ImageGallery>
        </Section> */}

        {/* Final Call to Action */}
        <Section delay="0.65s">
          <div style={{ 
            background: 'linear-gradient(135deg, #d97706, #92400e)', 
            borderRadius: '20px', 
            padding: '2rem', 
            textAlign: 'center',
            color: 'white'
          }}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Your Health Journey Starts Today</h3>
            <p style={{ marginBottom: '1.5rem', maxWidth: '600px', margin: '0 auto 1.5rem' }}>
              You don't have to lose all the weight to see benefits. Losing just 5-10% of your body weight can transform your health, reduce medications, and even reverse diabetes. Take the first step today.
            </p>
            <HeroButton onClick={handleAppointment} style={{ background: 'white', color: '#d97706' }}>
              Schedule Your Consultation Now
            </HeroButton>
          </div>
        </Section>
      </ContentContainer>
    </PageContainer>
  );
};

export default ObesityDiabetesPage;